import React, { useEffect, useMemo, useRef, useState } from "react";
import { Toast } from "primereact/toast";
import { RadioButton } from "primereact/radiobutton";
import { Dropdown } from "primereact/dropdown";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { ProgressSpinner } from "primereact/progressspinner";
import { Link } from "react-router-dom";
import { Panel } from "primereact/panel";
import { InputText } from "primereact/inputtext";
import { Dialog } from "primereact/dialog";
import { CustomButton } from "../../components/CustomButton";
import { GenerateBill } from "./generateBill";
import "./companies.css";
import { API_CREATE_NOTIFICATION } from "../../api/notification.service";
import {
  API_ALL_COMPANIES,
  API_ADD_NEW_COMPANY,
} from "../../api/company.service";

export const Companies = () => {
  const [companies, setCompanies] = useState([]);
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [search, setSearch] = useState("");
  const [visibleBill, setVisibleBill] = useState(false);
  const [visibleAddCompany, setVisibleAddCompany] = useState(false);
  const [loading, setLoading] = useState(false);
  const [visibleNotification, setVisibleNotification] = useState(false);
  const [notification, setNotification] = useState({
    companyId: [],
    isEnabled: true,
    message: "",
    type: null,
  });

  const toast = useRef(null);

  const notificationTypes = [
    { label: "Info", value: "info" },
    { label: "Warn", value: "warning" },
    { label: "Error", value: "error" },
    { label: "Success", value: "success" },
    { label: "Secondary", value: "secondary" },
    { label: "Contrast", value: "contrast" },
  ];

  const [newCompany, setNewCompany] = useState({
    userName: "",
    password: "",
    personalPhone: "",
    gupshupAppName: "",
    companyName: "",
    isActive: true,
    subscriptiontypeId: 1,
  });

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async (query = "") => {
    setLoading(true);
    try {
      const res = await API_ALL_COMPANIES(query);
      if (res?.companies?.rows) {
        setCompanies(res.companies.rows);
      }
    } catch (error) {
      console.error("Error fetching companies:", error);
    } finally {
      setLoading(false);
    }
  };

  const metrics = useMemo(() => {
    const activeCount = companies.filter((company) => Boolean(company.isActive)).length;
    return {
      total: companies.length,
      selected: selectedCompanies.length,
      active: activeCount,
      inactive: Math.max(companies.length - activeCount, 0),
    };
  }, [companies, selectedCompanies]);

  const handleAddNotification = async () => {
    if (!notification.message || !notification.type) {
      toast.current.show({
        severity: "warn",
        summary: "Missing Fields",
        detail: "Please fill all fields before saving.",
        life: 3000,
      });
      return;
    }

    setLoading(true);
    try {
      const payload = selectedCompanies.map((company) => ({
        companyId: company.id,
        type: notification.type,
        message: notification.message,
        isEnabled: notification.isEnabled,
      }));

      await API_CREATE_NOTIFICATION(payload);

      toast.current.show({
        severity: "success",
        summary: "Notification Created",
        detail: "Your notification was successfully added.",
        life: 3000,
      });
      setVisibleNotification(false);
      setNotification({
        companyId: [],
        isEnabled: true,
        message: "",
        type: null,
      });
    } catch (error) {
      console.error("Error creating notification:", error);
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to create notification. Try again.",
        life: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  const addCompanyDialog = () => (
    <Dialog
      header="Add New Company"
      visible={visibleAddCompany}
      style={{ width: "min(720px, 92vw)" }}
      onHide={() => setVisibleAddCompany(false)}
    >
      <div className="company-dialog-grid">
        <div className="company-dialog-field">
          <label htmlFor="companyName">Company Name</label>
          <InputText
            id="companyName"
            value={newCompany.companyName}
            onChange={(e) =>
              setNewCompany({ ...newCompany, companyName: e.target.value })
            }
          />
        </div>
        <div className="company-dialog-field">
          <label htmlFor="userName">User Name</label>
          <InputText
            id="userName"
            value={newCompany.userName}
            onChange={(e) =>
              setNewCompany({ ...newCompany, userName: e.target.value })
            }
          />
        </div>
        <div className="company-dialog-field">
          <label htmlFor="password">Password</label>
          <InputText
            id="password"
            type="password"
            value={newCompany.password}
            onChange={(e) =>
              setNewCompany({ ...newCompany, password: e.target.value })
            }
          />
        </div>
        <div className="company-dialog-field">
          <label htmlFor="personalPhone">Personal Phone</label>
          <InputText
            id="personalPhone"
            value={newCompany.personalPhone}
            onChange={(e) =>
              setNewCompany({ ...newCompany, personalPhone: e.target.value })
            }
          />
        </div>
      </div>
      <div className="company-dialog-actions">
        <CustomButton
          label="Cancel"
          varient="outline"
          onClick={() => setVisibleAddCompany(false)}
        />
        <CustomButton label="Save Company" onClick={addNewCompany} />
      </div>
    </Dialog>
  );

  const notificationDialog = () => (
    <Dialog
      header="Add Notification"
      visible={visibleNotification}
      style={{ width: "min(640px, 92vw)" }}
      onHide={() => setVisibleNotification(false)}
    >
      <div className="company-dialog-grid single">
        <div className="company-dialog-field">
          <label>Enable Notification</label>
          <div className="company-radio-row">
            <div className="company-radio-option">
              <RadioButton
                inputId="enabled"
                name="isEnabled"
                value={true}
                onChange={() =>
                  setNotification({ ...notification, isEnabled: true })
                }
                checked={notification.isEnabled}
              />
              <label htmlFor="enabled">Enabled</label>
            </div>

            <div className="company-radio-option">
              <RadioButton
                inputId="disabled"
                name="isEnabled"
                value={false}
                onChange={() =>
                  setNotification({ ...notification, isEnabled: false })
                }
                checked={!notification.isEnabled}
              />
              <label htmlFor="disabled">Disabled</label>
            </div>
          </div>
        </div>

        <div className="company-dialog-field">
          <label htmlFor="message">Message</label>
          <InputText
            id="message"
            value={notification.message}
            onChange={(e) =>
              setNotification({ ...notification, message: e.target.value })
            }
          />
        </div>

        <div className="company-dialog-field">
          <label htmlFor="type">Notification Type</label>
          <Dropdown
            id="type"
            value={notification.type}
            options={notificationTypes}
            onChange={(e) =>
              setNotification({ ...notification, type: e.value })
            }
            placeholder="Select Type"
          />
        </div>
      </div>

      <div className="company-dialog-actions">
        <CustomButton
          label="Cancel"
          varient="outline"
          onClick={() => setVisibleNotification(false)}
        />
        <CustomButton label="Save Notification" onClick={handleAddNotification} />
      </div>
    </Dialog>
  );

  const addNewCompany = async () => {
    setLoading(true);
    try {
      await API_ADD_NEW_COMPANY(newCompany);
      setVisibleAddCompany(false);
      setNewCompany({
        userName: "",
        password: "",
        personalPhone: "",
        gupshupAppName: "",
        companyName: "",
        isActive: false,
        subscriptiontypeId: 1,
      });
      fetchCompanies();
      toast.current.show({
        severity: "success",
        summary: "Success",
        detail: "Company added successfully!",
        life: 3000,
      });
    } catch (error) {
      console.error("Error adding company:", error);
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: `Error: ${error.message}`,
        life: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  const generateBill = () => {
    setVisibleBill(true);
  };

  const handleSearchCompanies = () => {
    setSelectedCompanies([]);
    fetchCompanies(search);
  };

  const convertDate = (company) => {
    const createdDate = new Date(company.createdAt);
    return <span>{createdDate.toLocaleString()}</span>;
  };

  const renderActiveStatus = (company) => (
    <span className={`company-status ${company.isActive ? "active" : "inactive"}`}>
      {company.isActive ? "Active" : "Inactive"}
    </span>
  );

  const moreInfoIcon = (company) => {
    return (
      <Link to={`/company/${company.id}`} className="company-link-icon">
        <i className="pi pi-arrow-up-right" />
      </Link>
    );
  };

  const viewBillPopup = () => {
    const selectedIds = selectedCompanies?.map((cmp) => cmp.id);
    const fileName =
      selectedCompanies.length > 0 ? selectedCompanies[0].companyName : "";

    return (
      <Dialog
        header="Usage Report"
        visible={visibleBill}
        style={{ width: "min(1100px, 94vw)" }}
        onHide={() => setVisibleBill(false)}
      >
        <GenerateBill props={{ companies: selectedIds, fileName }} />
      </Dialog>
    );
  };

  return (
    <div className="page-shell companies-page">
      <Toast ref={toast} />

      {loading ? (
        <div className="loading-container">
          <ProgressSpinner style={{ width: "50px", height: "50px" }} />
        </div>
      ) : null}

      {visibleBill && viewBillPopup()}
      {visibleAddCompany && addCompanyDialog()}
      {visibleNotification && notificationDialog()}

      <div className="page-header">
        <div className="page-header-main">
          <h1 className="page-title">Company portfolio</h1>
        </div>
        <div className="page-actions">
          <CustomButton
            label="Add Company"
            icon="pi pi-plus"
            onClick={() => setVisibleAddCompany(true)}
          />
          {selectedCompanies.length > 0 ? (
            <>
              <CustomButton
                label="Generate Usage"
                icon="pi pi-file"
                varient="outline"
                onClick={generateBill}
              />
              <CustomButton
                label="Add Notification"
                icon="pi pi-send"
                varient="outline"
                onClick={() => setVisibleNotification(true)}
              />
            </>
          ) : null}
        </div>
      </div>

      <div className="metric-grid company-metrics">
        <div className="metric-card">
          <span className="metric-card__label">Total Companies</span>
          <div className="metric-card__value">{metrics.total}</div>
        </div>
        <div className="metric-card">
          <span className="metric-card__label">Selected Records</span>
          <div className="metric-card__value">{metrics.selected}</div>
        </div>
        <div className="metric-card">
          <span className="metric-card__label">Active Accounts</span>
          <div className="metric-card__value">{metrics.active}</div>
        </div>
        <div className="metric-card">
          <span className="metric-card__label">Inactive Accounts</span>
          <div className="metric-card__value">{metrics.inactive}</div>
        </div>
      </div>

      <div className="surface-panel">
        <div className="toolbar-row companies-toolbar">
          <div className="toolbar-search">
            <InputText
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by company name"
            />
            <CustomButton
              label="Search"
              icon="pi pi-search"
              varient="outline"
              onClick={handleSearchCompanies}
            />
            <CustomButton
              label="Refresh"
              icon="pi pi-refresh"
              varient="outline"
              onClick={() => fetchCompanies()}
            />
          </div>
          {selectedCompanies.length > 0 ? (
            <span className="status-pill info">
              <i className="pi pi-check-square" />
              {selectedCompanies.length} company{selectedCompanies.length > 1 ? "ies" : ""} selected
            </span>
          ) : null}
        </div>

        <Panel className="companies-panel">
          <DataTable
            value={companies}
            paginator
            rows={8}
            rowsPerPageOptions={[8, 16, 24, 50]}
            tableStyle={{ minWidth: "60rem" }}
            selection={selectedCompanies}
            onSelectionChange={(e) => setSelectedCompanies(e.value)}
            className="companies-table"
          >
            <Column
              selectionMode="multiple"
              headerStyle={{ width: "3rem", textAlign: "center" }}
            />
            <Column field="id" header="ID" />
            <Column field="companyName" header="Name" />
            <Column field="phone" header="Phone" />
            <Column field="gupshupAppName" header="App Name" />
            <Column field="isActive" header="Status" body={renderActiveStatus} />
            <Column header="Created Date" body={convertDate} />
            <Column header="Open" body={moreInfoIcon} />
          </DataTable>
        </Panel>
      </div>
    </div>
  );
};
